import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { Platform } from '@ionic/angular';
import { CryptoApi, EncryptResponse } from '@perfood/capacitor-crypto-api';

interface StorageValue {
  value: EncryptResponse | string | null;
  persist: boolean;
}

const TAG = 'StorageServiceTag';
const SUPPORTED_ANDROID_SDK_VERSION = 31;
const ECDH_ALGORITHM = 'ecdh';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private storagePrefix = 'storage';

  constructor(private platform: Platform) {}

  public init(storagePrefix: string): void {
    this.storagePrefix = storagePrefix;
  }

  /**
   * Encrypt and save data to Capacitor Preferences. This uses a custom key prefix defined in environment.ts.
   *
   * @param {string} key Storage key
   * @param {unknown} value Value to be stored, and flag
   * which indicates if key should be removed during clearing.
   * Value accepts string, number, boolean, null, undefined and JSON.
   * @param persist if true, value will be persisted after clearing the storage.
   * @returns {Promise<void>}
   */
  public async set(key: string, value: unknown, persist = false): Promise<void> {
    const plaintext = this.valueToString(value);

    let valueToSave: EncryptResponse | string | null = null;
    const androidSDKVersion = (await Device.getInfo())?.androidSDKVersion;

    // encryption and decryption on android is only supported for sdk version >= 31
    if (!this.platform.is('hybrid') || (androidSDKVersion && androidSDKVersion < SUPPORTED_ANDROID_SDK_VERSION)) {
      valueToSave = plaintext;
    } else {
      const foreignPublicKey = (await CryptoApi.generateKey({ tag: TAG, algorithm: ECDH_ALGORITHM })).publicKey;
      if (!foreignPublicKey) {
        return;
      }
      valueToSave = plaintext ? await CryptoApi.encrypt({ tag: TAG, foreignPublicKey, plaintext }) : null;
    }

    return Preferences.set({
      key: this.getStorageKey(key),
      value: JSON.stringify({
        value: valueToSave,
        persist,
      }),
    });
  }

  /**
   * Get stored value by key
   * @param {string} key Storage key
   * @param {unknown} [defaultValue] Default value to be used if key dows not exist in storage
   * @returns {Promise<void>}
   */
  public async get(key: string, defaultValue?: unknown): Promise<unknown | undefined> {
    const { value } = await Preferences.get({ key: this.getStorageKey(key) });
    // catch null and undefined values
    if (!value) {
      return defaultValue;
    }

    const parsedValue = this.parseJsonIfNeeded(value);
    const storedValue = (parsedValue as StorageValue).value;

    // catch null
    if (!storedValue) {
      return defaultValue;
    }

    const androidSDKVersion = (await Device.getInfo())?.androidSDKVersion;

    const isString = typeof storedValue === 'string' || storedValue instanceof String;
    // encryption and decryption on android is only supported for sdk version >= 31
    if (isString || (androidSDKVersion && androidSDKVersion < SUPPORTED_ANDROID_SDK_VERSION)) {
      return this.parseJsonIfNeeded(storedValue as string);
    }

    const foreignPublicKey = (await CryptoApi.generateKey({ tag: TAG, algorithm: ECDH_ALGORITHM })).publicKey;
    if (!foreignPublicKey) {
      return;
    }

    const encryptResponse = storedValue as EncryptResponse;
    const plaintext = (
      await CryptoApi.decrypt({
        tag: TAG,
        foreignPublicKey,
        iv: encryptResponse.iv,
        ciphertext: encryptResponse.ciphertext,
      })
    ).plaintext;

    if (!plaintext) {
      return defaultValue;
    }

    return this.parseJsonIfNeeded(plaintext);
  }

  public async remove(key: string): Promise<void> {
    return await Preferences.remove({ key: this.getStorageKey(key) });
  }

  private getStorageKey(key: string): string {
    return `${this.storagePrefix}_${key}`;
  }

  private valueToString(value: unknown): string | null {
    if (value === undefined || value === null) return null;

    switch (typeof value) {
      case 'function':
        throw new Error('Yeah, so, functions cannot be saved into localStorage.');
      case 'string':
      case 'number':
      case 'boolean':
        return value.toString();
      case 'object':
        return JSON.stringify(value);
    }

    return null;
  }

  private parseJsonIfNeeded(item: string): unknown {
    try {
      return JSON.parse(item);
    } catch (e) {
      // Item is probably a string like 'Hello world' which cannot be parsed,
      // Also: undefined, and null are being persisted as string 'undefined' and 'null' with localStorage.set()
      // no hard feelings, just don't let the app crash
    }

    return item;
  }
}
